import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import { useSportConfig } from '../context/SportConfig';
import '../styles/AdminTournaments.css';

const CATEGORIES = ['Grand Slam', 'Masters 1000'];
const SURFACES = ['Hard', 'Clay', 'Grass'];

const EMPTY_FORM = {
  name: '',
  category: 'Grand Slam',
  surface: 'Hard',
  location: '',
  startDate: '',
  endDate: '',
};

function toDateInput(isoString) {
  if (!isoString) return '';
  return isoString.slice(0, 10);
}

function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function AdminTournaments() {
  const sportConfig = useSportConfig();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state: null | { mode: 'add' } | { mode: 'edit', tournament }
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formStatus, setFormStatus] = useState(null); // { type, message }
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { tournament }
  const [deleteStatus, setDeleteStatus] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/tournaments`);
      setTournaments(res.data.tournaments);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch tournaments');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormStatus(null);
    setModal({ mode: 'add' });
  };

  const openEdit = useCallback((tournament) => {
    setForm({
      name: tournament.name,
      category: tournament.category,
      surface: tournament.surface,
      location: tournament.location,
      startDate: toDateInput(tournament.startDate),
      endDate: toDateInput(tournament.endDate),
    });
    setFormStatus(null);
    setModal({ mode: 'edit', tournament });
  }, []);

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormStatus({ type: 'error', message: 'Name is required' }); return; }
    if (!form.location.trim()) { setFormStatus({ type: 'error', message: 'Location is required' }); return; }
    if (!form.startDate) { setFormStatus({ type: 'error', message: 'Start date is required' }); return; }
    if (!form.endDate) { setFormStatus({ type: 'error', message: 'End date is required' }); return; }
    if (form.endDate <= form.startDate) { setFormStatus({ type: 'error', message: 'End date must be after start date' }); return; }

    try {
      setSaving(true);
      setFormStatus(null);
      if (modal.mode === 'add') {
        await axios.post(`${API_URL}/admin/tournaments`, form);
        setFormStatus({ type: 'success', message: 'Tournament created!' });
      } else {
        await axios.put(`${API_URL}/admin/tournaments/${modal.tournament._id}`, form);
        setFormStatus({ type: 'success', message: 'Tournament updated!' });
      }
      fetchTournaments();
      setTimeout(() => { setModal(null); setFormStatus(null); }, 1200);
    } catch (err) {
      setFormStatus({ type: 'error', message: err.response?.data?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API_URL}/admin/tournaments/${deleteConfirm.tournament._id}`);
      setDeleteConfirm(null);
      fetchTournaments();
    } catch (err) {
      setDeleteStatus(err.response?.data?.message || 'Delete failed');
    }
  };

  const isPast = (endDate) => new Date(endDate) < new Date();

  const ActionsCell = useCallback((params) => (
    <div className="tournament-actions">
      <button className="btn-edit-tournament" onClick={() => openEdit(params.data)}>Edit</button>
      <button className="btn-delete-tournament" onClick={() => { setDeleteStatus(null); setDeleteConfirm({ tournament: params.data }); }}>Delete</button>
    </div>
  ), [openEdit]);

  const StatusCell = useCallback((params) => {
    const past = isPast(params.data.endDate);
    return <span className={`tournament-status-badge ${past ? 'status-past' : 'status-upcoming'}`}>{past ? 'Past' : 'Upcoming'}</span>;
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Name',
      field: 'name',
      sortable: true,
      filter: true,
      minWidth: 220,
      sort: 'asc',
      sortIndex: 1,
    },
    {
      headerName: 'Category',
      field: 'category',
      sortable: true,
      filter: true,
      maxWidth: 140,
    },
    {
      headerName: 'Surface',
      field: 'surface',
      sortable: true,
      filter: true,
      maxWidth: 100,
    },
    {
      headerName: 'Location',
      field: 'location',
      sortable: true,
      filter: true,
      minWidth: 160,
    },
    {
      headerName: 'Start Date',
      field: 'startDate',
      sortable: true,
      sort: 'asc',
      sortIndex: 0,
      maxWidth: 130,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: 'End Date',
      field: 'endDate',
      sortable: true,
      maxWidth: 130,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: 'Status',
      cellRenderer: StatusCell,
      sortable: false,
      filter: false,
      maxWidth: 110,
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCell,
      sortable: false,
      filter: false,
      minWidth: 140,
    },
  ], [ActionsCell, StatusCell]);

  const defaultColDef = useMemo(() => ({ resizable: true, flex: 1 }), []);

  if (loading) return <div className="admin-loading">Loading tournaments...</div>;
  if (error) return <div className="admin-error">{error}</div>;

  const upcomingCount = tournaments.filter(t => !isPast(t.endDate)).length;
  const pastCount = tournaments.length - upcomingCount;

  return (
    <div className="admin-tournaments-section">
      <div className="admin-tournaments-header">
        <div>
          <h3>🏆 Site Admin — Tournaments ({tournaments.length})</h3>
          <p className="tournaments-summary">{upcomingCount} upcoming · {pastCount} past</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Link to="/dashboard" className="btn btn-secondary">← Dashboard</Link>
          <button className="btn btn-add-tournament" onClick={openAdd}>+ Add Tournament</button>
        </div>
      </div>

      <div className="ag-theme-alpine admin-grid">
        <AgGridReact
          rowData={tournaments}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={20}
          domLayout="autoHeight"
          animateRows={true}
          getRowClass={(params) => isPast(params.data.endDate) ? 'tournament-row-past' : ''}
        />
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content tournament-modal" onClick={e => e.stopPropagation()}>
            <h4>{modal.mode === 'add' ? 'Add Tournament' : `Edit: ${modal.tournament.name}`}</h4>

            <label className="modal-label">
              Name
              <input
                type="text"
                value={form.name}
                onChange={e => handleFormChange('name', e.target.value)}
                placeholder="e.g. Wimbledon 2026"
              />
            </label>

            <div className="modal-row">
              <label className="modal-label">
                Category
                <select value={form.category} onChange={e => handleFormChange('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="modal-label">
                Surface
                <select value={form.surface} onChange={e => handleFormChange('surface', e.target.value)}>
                  {SURFACES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <label className="modal-label">
              Location
              <input
                type="text"
                value={form.location}
                onChange={e => handleFormChange('location', e.target.value)}
                placeholder="e.g. London, England"
              />
            </label>

            <div className="modal-row">
              <label className="modal-label">
                Start Date
                <input
                  type="date"
                  value={form.startDate}
                  min={today}
                  onChange={e => handleFormChange('startDate', e.target.value)}
                />
              </label>
              <label className="modal-label">
                End Date
                <input
                  type="date"
                  value={form.endDate}
                  min={form.startDate || today}
                  onChange={e => handleFormChange('endDate', e.target.value)}
                />
              </label>
            </div>

            {formStatus && (
              <div className={`form-status ${formStatus.type}`}>{formStatus.message}</div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : modal.mode === 'add' ? 'Create' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h4>Delete Tournament?</h4>
            <p className="delete-warning">
              Are you sure you want to delete <strong>{deleteConfirm.tournament.name}</strong>?
              This cannot be undone. Deletion is blocked if any leagues are using this tournament.
            </p>
            {deleteStatus && <div className="form-status error">{deleteStatus}</div>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTournaments;
